import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import setupService from "../features/setup/setupService";
import organisationService from "../features/organisation/organisationService";

/**
 * Ranking Setup: every scoring table, the settings row, and per-organisation
 * overrides. Local state only, like the other admin tabs.
 */

// ------------------------------------------------------------------ helpers

function IdSelect({ label, value, onChange, options, fullWidth = true }) {
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={onChange}
      fullWidth={fullWidth}
      size="small"
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

// Generic CRUD section: add / edit-in-dialog / delete (with usage conflict).
function CrudSection({ title, table, rows, columns, lookup, onChanged }) {
  const [dialog, setDialog] = useState(null); // null | { mode: "new" } | { mode: "edit", row }
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // row pending delete
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const openNew = () => {
    setForm({});
    setError("");
    setDialog({ mode: "new" });
  };

  const openEdit = (row) => {
    const initial = {};
    columns.forEach((column) => {
      initial[column.key] = row[column.key] ?? "";
    });
    setForm(initial);
    setError("");
    setDialog({ mode: "edit", row });
  };

  const save = async () => {
    setBusy(true);
    setError("");

    // Drop dependent-column values that are no longer valid options
    // (e.g. a position from a different tier after the category changed).
    const payload = { ...form };
    for (const column of columns) {
      if (column.optionsFor) {
        const options = column.optionsFor(payload);
        if (
          !options.some(
            (option) => String(option.id) === String(payload[column.key]),
          )
        ) {
          delete payload[column.key];
        }
      }
    }

    try {
      if (dialog.mode === "new") {
        await setupService.createSetupRow(table, payload);
      } else {
        await setupService.updateSetupRow(table, dialog.row.id, payload);
      }
      setDialog(null);
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = (row) => {
    setDeleteError("");
    setConfirmDelete(row);
  };

  const confirmRemove = async () => {
    setDeleting(true);
    setDeleteError("");

    try {
      await setupService.deleteSetupRow(table, confirmDelete.id);
      setConfirmDelete(null);
      await onChanged();
    } catch (err) {
      const references = err.response?.data?.references;
      const message = references?.length
        ? `Still in use by: ${references.join(", ")}`
        : err.response?.data?.message || err.message || "Delete failed";
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  const setField = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Button variant="contained" size="small" onClick={openNew}>
          Add
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key}>{column.label}</TableCell>
            ))}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center">
                <Typography variant="body2" color="text.secondary">
                  Nothing here yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {column.render
                    ? column.render(row[column.key], row)
                    : row[column.key] ?? "-"}
                </TableCell>
              ))}
              <TableCell align="right">
                <Button size="small" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button size="small" color="error" onClick={() => remove(row)}>
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={dialog !== null} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialog?.mode === "new" ? `Add ${title.toLowerCase()}` : `Edit ${title.toLowerCase()}`}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {columns.map((column) =>
              column.select || column.optionsFor ? (
                (() => {
                  // Dependent selects: optionsFor(form) narrows the list
                  // based on other fields (e.g. positions for the category's
                  // tier). Values no longer in the list render as empty.
                  const options = column.optionsFor
                    ? column.optionsFor(form)
                    : column.select;
                  const valid = options.some(
                    (option) =>
                      String(option.id) === String(form[column.key]),
                  );
                  return (
                    <IdSelect
                      key={column.key}
                      label={column.label}
                      value={valid ? form[column.key] : ""}
                      onChange={setField(column.key)}
                      options={options}
                    />
                  );
                })()
              ) : (
                <TextField
                  key={column.key}
                  label={column.label}
                  type={column.type || "text"}
                  value={form[column.key] ?? ""}
                  onChange={setField(column.key)}
                  fullWidth
                  size="small"
                />
              ),
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={busy}>
            {busy ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation - errors (e.g. rows still referenced) are
          shown in-dialog instead of a browser alert. */}
      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete {title.toLowerCase()}</DialogTitle>
        <DialogContent dividers>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body2">
            Delete "{confirmDelete?.name || confirmDelete?.id}"? This cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRemove}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Position % matrix: tiers x positions.
function PositionMatrix({ tiers, positions, tierPositions, onChanged }) {
  const [draft, setDraft] = useState({}); // `${tier}_${position}` -> value
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const initial = {};
    tierPositions.forEach((row) => {
      initial[`${row.tier}_${row.position}`] = { value: row.allocation, id: row.id };
    });
    setDraft(initial);
  }, [tierPositions]);

  const setCell = (tier, position) => (event) => {
    const key = `${tier}_${position}`;
    setDraft((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), value: event.target.value },
    }));
  };

  const saveAll = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      let saved = 0;

      for (const [key, cell] of Object.entries(draft)) {
        const [tier, position] = key.split("_").map(Number);
        const payload = { tier, position, allocation: Number(cell.value) };

        if (Number.isNaN(payload.allocation)) continue;

        if (cell.id) {
          await setupService.updateSetupRow("tier_position", cell.id, payload);
        } else {
          await setupService.createSetupRow("tier_position", payload);
        }
        saved += 1;
      }

      setNotice(`Saved ${saved} position percentages`);
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Position percentages (per tier)</Typography>
        <Button variant="contained" size="small" onClick={saveAll} disabled={busy}>
          {busy ? "Saving..." : "Save all"}
        </Button>
      </Box>

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tier</TableCell>
              {positions.map((position) => (
                <TableCell key={position.id} align="center">
                  {position.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.map((tier) => (
              <TableRow key={tier.id} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {tier.name}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {tier.description}
                  </Typography>
                </TableCell>
                {positions.map((position) => {
                  const key = `${tier.id}_${position.id}`;
                  return (
                    <TableCell key={position.id} align="center" sx={{ width: 90 }}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        value={draft[key]?.value ?? ""}
                        onChange={setCell(tier.id, position.id)}
                        placeholder="-"
                        sx={{ width: 72 }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// Settings: base points + year weights.
function SettingsSection({ settings, onChanged }) {
  const [form, setForm] = useState({
    base_points: "",
    first_year_allocation: "",
    second_year_allocation: "",
    third_year_allocation: "",
  });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!settings) return;
    setForm({
      base_points: settings.base_points ?? "",
      first_year_allocation: settings.first_year_allocation ?? "",
      second_year_allocation: settings.second_year_allocation ?? "",
      third_year_allocation: settings.third_year_allocation ?? "",
    });
  }, [settings]);

  const save = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      await setupService.updateSettings(form);
      setNotice("Settings saved. Stored entry points are unchanged until you run Recalculate.");
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Settings
      </Typography>

      {notice && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Base points"
          type="number"
          value={form.base_points}
          onChange={(e) => setForm((p) => ({ ...p, base_points: e.target.value }))}
          size="small"
        />
        <TextField
          label="1st year allocation %"
          type="number"
          value={form.first_year_allocation}
          onChange={(e) => setForm((p) => ({ ...p, first_year_allocation: e.target.value }))}
          size="small"
        />
        <TextField
          label="2nd year allocation %"
          type="number"
          value={form.second_year_allocation}
          onChange={(e) => setForm((p) => ({ ...p, second_year_allocation: e.target.value }))}
          size="small"
        />
        <TextField
          label="3rd year allocation %"
          type="number"
          value={form.third_year_allocation}
          onChange={(e) => setForm((p) => ({ ...p, third_year_allocation: e.target.value }))}
          size="small"
        />
      </Stack>

      <Button variant="contained" size="small" onClick={save} disabled={busy}>
        {busy ? "Saving..." : "Save settings"}
      </Button>
    </Box>
  );
}

// Organisation overrides editor.
function OverridesSection({ overview }) {
  const [organisations, setOrganisations] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [effective, setEffective] = useState(null);
  const [draft, setDraft] = useState({}); // key -> string ("" = untouched)
  const [resets, setResets] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    organisationService
      .listOrganisations()
      .then((response) => setOrganisations(response.data.organisations || []))
      .catch(() => setOrganisations([]));
  }, []);

  const system = useMemo(() => {
    const settings = overview?.settings?.[0] || {};
    const positions = {};
    (overview?.tierPositions || []).forEach((row) => {
      positions[String(row.tier)] = positions[String(row.tier)] || {};
      positions[String(row.tier)][String(row.position)] = Number(row.allocation);
    });

    return {
      basePoints: Number(settings.base_points),
      yearWeights: [
        Number(settings.first_year_allocation),
        Number(settings.second_year_allocation),
        Number(settings.third_year_allocation),
      ],
      tiers: Object.fromEntries((overview?.tiers || []).map((r) => [String(r.id), Number(r.allocation)])),
      formats: Object.fromEntries((overview?.formats || []).map((r) => [String(r.id), Number(r.allocation)])),
      categoryTypes: Object.fromEntries((overview?.categoryTypes || []).map((r) => [String(r.id), Number(r.allocation)])),
      positions,
    };
  }, [overview]);

  const loadOrg = async (id) => {
    setOrgId(id);
    setDraft({});
    setResets(new Set());
    setNotice("");
    setError("");

    if (!id) {
      setEffective(null);
      return;
    }

    try {
      const response = await setupService.getEffectiveAllocations(id);
      setEffective(response.data.effective || system);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load");
    }
  };

  const setDraftValue = (key) => (event) =>
    setDraft((prev) => ({ ...prev, [key]: event.target.value }));

  const markReset = (key) => {
    setResets((prev) => new Set(prev).add(key));
    setDraft((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      const payload = {};

      const push = (shape, key, value) => {
        if (value === undefined) return;
        shape[key] = value;
      };

      const numberAt = (value) => {
        if (value === "" || value === null || value === undefined) return undefined;
        const n = Number(value);
        return Number.isFinite(n) ? n : undefined;
      };

      // Scalars
      push(payload, "base_points", resets.has("base_points") ? null : numberAt(draft.base_points));
      ["first_year_allocation", "second_year_allocation", "third_year_allocation"].forEach((key) => {
        push(payload, key, resets.has(key) ? null : numberAt(draft[key]));
      });

      // Id maps
      const collectMap = (mapName, keys) => {
        const map = {};
        keys.forEach((key) => {
          const value = resets.has(key) ? null : numberAt(draft[key]);
          if (value !== undefined) map[`${key.split(":")[1]}`] = value;
        });
        if (Object.keys(map).length > 0) payload[mapName] = map;
      };

      collectMap("tiers", (overview?.tiers || []).map((r) => `tier:${r.id}`));
      collectMap("formats", (overview?.formats || []).map((r) => `format:${r.id}`));
      collectMap("category_types", (overview?.categoryTypes || []).map((r) => `category_type:${r.id}`));

      // Positions
      const positions = {};
      (overview?.tiers || []).forEach((tier) => {
        (overview?.positions || []).forEach((position) => {
          const key = `position:${tier.id}:${position.id}`;
          const value = resets.has(key) ? null : numberAt(draft[key]);
          if (value !== undefined) {
            positions[String(tier.id)] = positions[String(tier.id)] || {};
            positions[String(tier.id)][String(position.id)] = value;
          }
        });
      });
      if (Object.keys(positions).length > 0) payload.positions = positions;

      const response = await setupService.saveOverrides(orgId, payload);
      setEffective(response.data.effective || system);
      setDraft({});
      setResets(new Set());
      setNotice("Overrides saved.");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!effective) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Organisation overrides
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <IdSelect
          label="Organisation"
          value={orgId}
          onChange={(e) => loadOrg(e.target.value)}
          options={organisations}
          fullWidth={false}
        />
        {orgId && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>This organisation has no overrides - it uses the system defaults above.</Typography>}
      </Box>
    );
  }

  const OverrideRow = ({ label, systemValue, effectiveValue, draftKey }) => (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell align="right">{systemValue}</TableCell>
      <TableCell align="right">{effectiveValue}</TableCell>
      <TableCell align="center" sx={{ width: 120 }}>
        <TextField
          size="small"
          type="number"
          inputProps={{ min: 0, max: 100 }}
          value={draft[draftKey] ?? ""}
          onChange={setDraftValue(draftKey)}
          placeholder="system"
          sx={{ width: 90 }}
        />
      </TableCell>
      <TableCell align="center" sx={{ width: 90 }}>
        <Tooltip title="Reset to system default">
          <Button size="small" onClick={() => markReset(draftKey)} disabled={resets.has(draftKey)}>
            Reset
          </Button>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
        <Typography variant="h6">Organisation overrides</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <IdSelect
            label="Organisation"
            value={orgId}
            onChange={(e) => loadOrg(e.target.value)}
            options={organisations}
            fullWidth={false}
          />
          <Button variant="contained" size="small" onClick={save} disabled={busy}>
            {busy ? "Saving..." : "Save overrides"}
          </Button>
        </Stack>
      </Box>

      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Value</TableCell>
            <TableCell align="right">System</TableCell>
            <TableCell align="right">Effective</TableCell>
            <TableCell align="center">Override</TableCell>
            <TableCell align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          <OverrideRow label="Base points" systemValue={system.basePoints} effectiveValue={effective.basePoints} draftKey="base_points" />
          {["first_year_allocation", "second_year_allocation", "third_year_allocation"].map((key, index) => (
            <OverrideRow
              key={key}
              label={`Year ${index + 1} weight %`}
              systemValue={system.yearWeights[index]}
              effectiveValue={effective.yearWeights[index]}
              draftKey={key}
            />
          ))}
          {(overview?.tiers || []).map((tier) => (
            <OverrideRow
              key={`tier:${tier.id}`}
              label={`Tier: ${tier.name}`}
              systemValue={system.tiers[String(tier.id)]}
              effectiveValue={effective.tiers[String(tier.id)]}
              draftKey={`tier:${tier.id}`}
            />
          ))}
          {(overview?.formats || []).map((format) => (
            <OverrideRow
              key={`format:${format.id}`}
              label={`Format: ${format.name}`}
              systemValue={system.formats[String(format.id)]}
              effectiveValue={effective.formats[String(format.id)]}
              draftKey={`format:${format.id}`}
            />
          ))}
          {(overview?.categoryTypes || []).map((categoryType) => (
            <OverrideRow
              key={`category_type:${categoryType.id}`}
              label={`Category type: ${categoryType.name}`}
              systemValue={system.categoryTypes[String(categoryType.id)]}
              effectiveValue={effective.categoryTypes[String(categoryType.id)]}
              draftKey={`category_type:${categoryType.id}`}
            />
          ))}
          {(overview?.tiers || []).flatMap((tier) =>
            (overview?.positions || []).map((position) => (
              <OverrideRow
                key={`position:${tier.id}:${position.id}`}
                label={`Position: ${tier.name} - ${position.name}`}
                systemValue={system.positions[String(tier.id)]?.[String(position.id)] ?? "n/a"}
                effectiveValue={effective.positions[String(tier.id)]?.[String(position.id)] ?? "n/a"}
                draftKey={`position:${tier.id}:${position.id}`}
              />
            )),
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// ------------------------------------------------------------------ page

const SECTIONS = [
  "Settings",
  "Levels",
  "Categories",
  "Category Types",
  "Tiers",
  "Formats",
  "Positions",
  "Position %",
  "Level Category Types",
  "Category Formats",
  "Category Position Exclusions",
  "Organisation Overrides",
];

export default function RankingSetup() {
  const [section, setSection] = useState(0);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");

    try {
      const response = await setupService.getSetupOverview();
      setOverview(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load setup data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!overview) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || "Could not load setup data."}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Ranking Setup
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The scoring tables behind the rankings. Changes to system values affect
        new entries immediately; existing entry points only change when you run
        Recalculate under Admin Actions. Organisation overrides apply only to
        that organisation's own public page.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Tabs
        value={section}
        onChange={(_, value) => setSection(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {SECTIONS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {section === 0 && <SettingsSection settings={overview.settings[0]} onChanged={load} />}

      {section === 1 && (
        <CrudSection
          title="Levels"
          table="level"
          rows={overview.levels}
          columns={[{ key: "name", label: "Name" }]}
          onChanged={load}
        />
      )}

      {section === 2 && (
        <CrudSection
          title="Categories"
          table="categories"
          rows={overview.categories}
          columns={[
            { key: "name", label: "Name" },
            {
              key: "tier",
              label: "Tier",
              select: overview.tiers,
              render: (value) => overview.tiers.find((t) => t.id === value)?.name ?? value,
            },
          ]}
          onChanged={load}
        />
      )}

      {section === 3 && (
        <CrudSection
          title="Category Types"
          table="category_type"
          rows={overview.categoryTypes}
          columns={[
            { key: "name", label: "Name" },
            { key: "allocation", label: "Allocation %", type: "number" },
          ]}
          onChanged={load}
        />
      )}

      {section === 4 && (
        <CrudSection
          title="Tiers"
          table="tiers"
          rows={overview.tiers}
          columns={[
            { key: "name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "allocation", label: "Allocation %", type: "number" },
          ]}
          onChanged={load}
        />
      )}

      {section === 5 && (
        <CrudSection
          title="Formats"
          table="formats"
          rows={overview.formats}
          columns={[
            { key: "name", label: "Name" },
            { key: "allocation", label: "Allocation %", type: "number" },
          ]}
          onChanged={load}
        />
      )}

      {section === 6 && (
        <CrudSection
          title="Positions"
          table="positions"
          rows={overview.positions}
          columns={[{ key: "name", label: "Name" }]}
          onChanged={load}
        />
      )}

      {section === 7 && (
        <PositionMatrix
          tiers={overview.tiers}
          positions={overview.positions}
          tierPositions={overview.tierPositions}
          onChanged={load}
        />
      )}

      {section === 8 && (
        <CrudSection
          title="Level Category Types"
          table="level_category_type"
          rows={overview.levelCategoryTypes}
          columns={[
            {
              key: "level",
              label: "Level",
              select: overview.levels,
              render: (value) => overview.levels.find((l) => l.id === value)?.name ?? value,
            },
            {
              key: "category_type",
              label: "Category Type",
              select: overview.categoryTypes,
              render: (value) => overview.categoryTypes.find((c) => c.id === value)?.name ?? value,
            },
            { key: "allocation", label: "Allocation %", type: "number" },
          ]}
          onChanged={load}
        />
      )}

      {section === 9 && (
        <CrudSection
          title="Category Formats"
          table="category_formats"
          rows={overview.categoryFormats}
          columns={[
            {
              key: "category_id",
              label: "Category",
              select: overview.categories,
              render: (value) => overview.categories.find((c) => c.id === value)?.name ?? value,
            },
            {
              key: "format_id",
              label: "Format",
              select: overview.formats,
              render: (value) => overview.formats.find((f) => f.id === value)?.name ?? value,
            },
          ]}
          onChanged={load}
        />
      )}

      {section === 10 && (
        <CrudSection
          title="Category Position Exclusions"
          table="category_position_exclusions"
          rows={overview.categoryPositionExclusions}
          columns={[
            {
              key: "category_id",
              label: "Category",
              select: overview.categories,
              render: (value) =>
                overview.categories.find((c) => c.id === value)?.name ?? value,
            },
            {
              key: "position_id",
              label: "Position",
              // Positions auto-populate from the selected category's tier
              // (tier_position rows), mirroring the Add Entry form.
              optionsFor: (form) => {
                const category = overview.categories.find(
                  (c) => String(c.id) === String(form.category_id),
                );
                if (!category) return [];
                const tierPositionIds = new Set(
                  overview.tierPositions
                    .filter(
                      (tp) => String(tp.tier) === String(category.tier),
                    )
                    .map((tp) => tp.position),
                );
                return overview.positions.filter((p) =>
                  tierPositionIds.has(p.id),
                );
              },
              render: (value) =>
                overview.positions.find((p) => p.id === value)?.name ?? value,
            },
          ]}
          onChanged={load}
        />
      )}

      {section === 11 && <OverridesSection overview={overview} />}
    </Box>
  );
}
